import { Badge, Box, Group, Stack, Text } from '@mantine/core';
import { TechnologyExperience } from '../../../../../models/technologyExperience.model';
import { PersonTechnology } from '../../../../../types/technology';

interface CategorizedSkillsProps {
    technologies: PersonTechnology[];
    technologyExperience: TechnologyExperience[];
}

// Helper function to get color by category
const getColorByCategory = (tech: PersonTechnology) => {
    if (!tech.technology.categories || tech.technology.categories.length === 0) {
        return 'gray';
    }

    const category = tech.technology.categories[0];

    // Define available Material Design colors from our palette
    const materialColors = [
        'materialBlue', 'materialTeal', 'materialOrange', 'materialPurple', 'materialRed',
        'materialAmber', 'materialCyan', 'materialGray', 'materialGreen', 'sopraBlue',
        'sopraPurple', 'sopraOrange', 'sopraGreen', 'materialDeepPurple', 'brand',
        'indigo', 'violet', 'grape', 'pink', 'lime', 'dark'
    ];

    // Use category ID if available for consistent color assignment
    if (category.id) {
        return materialColors[category.id % materialColors.length];
    }

    // Fallback: use category name hash for consistent color assignment
    const hash = category.name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return materialColors[hash % materialColors.length];
};

export function CategorizedSkills({ technologies, technologyExperience }: CategorizedSkillsProps) {
    // Create a map of technology names to their experience
    const experienceMap = new Map(
        technologyExperience.map(exp => [exp.technologyName, exp.yearsOfExperience])
    );

    const grouped = technologies.reduce((acc, tech) => {
        if (tech.technology?.categories && tech.technology.categories.length > 0) {
            tech.technology.categories.forEach(category => {
                if (!acc[category.name]) {
                    acc[category.name] = [];
                }
                acc[category.name].push(tech);
            });
        } else {
            if (!acc['Uncategorized']) {
                acc['Uncategorized'] = [];
            }
            acc['Uncategorized'].push(tech);
        }
        return acc;
    }, {} as Record<string, PersonTechnology[]>);

    // Sort technologies within each category by experience
    Object.keys(grouped).forEach(categoryName => {
        grouped[categoryName] = grouped[categoryName].sort((a, b) => {
            const expA = experienceMap.get(a.technology?.name || '') || 0;
            const expB = experienceMap.get(b.technology?.name || '') || 0;
            return expB - expA;
        });
    });

    return (
        <Stack gap="lg">
            {Object.entries(grouped).map(([categoryName, techs]) => (
                <Box key={categoryName}>
                    <Text fw={600} size="sm" mb="xs">{categoryName}</Text>
                    <Group gap="xs">
                        {techs.map(tech => {
                            const experience = experienceMap.get(tech.technology?.name || '');

                            return (
                                <Badge
                                    key={tech.id}
                                    size="sm"
                                    color={getColorByCategory(tech)}
                                    variant={tech.favourite ? "filled" : "light"}
                                    radius="md"
                                >
                                    {tech.technology?.name || 'Unknown Technology'}
                                    {experience !== undefined && ` (${experience.toFixed(1)}y)`}
                                </Badge>
                            );
                        })}
                    </Group>
                </Box>
            ))}
        </Stack>
    );
}

